import React, { useCallback, useEffect, useRef, useState } from "react";

import { format, parseISO } from "date-fns";
import { Eye, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { cn } from "../../lib/utils";

import MarkdownWrapper from "../MarkdownWrapper";
import Audio from "../Audio";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// Ler a conversa sem assumir o atendimento. É o que um supervisor precisa
// para decidir se entra: abrir o ticket de verdade o marcaria como lido e
// tiraria a conversa da fila de quem ia atender.
const TicketPeekDialog = ({ ticket }) => {
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [carregando, setCarregando] = useState(false);

  const fimRef = useRef(null);
  // Referência para o "tap blanket": div transparente full-screen que absorve
  // o pointer-up remanescente após o fechamento do modal. Sem ela, o pointerdown
  // no overlay fecha o Dialog e o pointerup cai SOBRE a linha do TicketListItem
  // (o overlay já tinha sido desmontado antes do pointerup), disparando o clique
  // que abre o ticket em tela cheia — era o bug "cliquei fora, abriu o ticket
  // e apareceu "An error occurred" (porque o React estava trocou de rota no meio
  // do fechamento do modal.
  const blanketRef = useRef(null);

  const cobrirCliquePassou = () => {
    const el = document.createElement("div");
    const estilo = el.style;
    estilo.position = "fixed";
    estilo.inset = "0px";
    estilo.zIndex = "2147483647";
    estilo.pointerEvents = "auto";
    estilo.background = "transparent";
    document.body.appendChild(el);
    blanketRef.current = el;
    window.setTimeout(() => {
      if (blanketRef.current === el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
      if (blanketRef.current === el) blanketRef.current = null;
    }, 160);
  };

  useEffect(() => {
    return () => {
      // Desmontou o componente: tira o blanket se ele ainda existia.
      if (blanketRef.current?.parentNode) blanketRef.current.parentNode.removeChild(blanketRef.current);
      blanketRef.current = null;
    };
  }, []);

  const fecharDialogo = () => {
    setAberto(false);
    // Assim que o close disparar, cobre o clique de 160ms para não passar
    // por baixo para o TicketListItem que está no fundo.
    cobrirCliquePassou();
  };

  const carregar = useCallback(async () => {
    setCarregando(true);
    const abortController = new AbortController();
    try {
      const { data } = await api.get(`/messages/${ticket.id}`, {
        params: { pageNumber: 1, peek: true },
        signal: abortController.signal,
      });
      setMensagens(data.messages || []);
    } catch (err) {
      // Cancelamento não é erro: acontece quando o componente desmonta
      // no meio do request (navegação, modal fechado). Não mostra toast
      // para o usuário porque ele não fez nada de errado. Sem isso aparecia
      // "An error occurred!" em branco do toastError default.
      const cancelado =
        err?.code === "ERR_CANCELED" ||
        err?.code === "ECONNABORTED" ||
        err?.name === "CanceledError" ||
        (err && err.message && /cancel(ed|lation)/i.test(err.message));
      if (!cancelado) {
        toastError(err);
      }
    }
    setCarregando(false);
    return () => abortController.abort();
  }, [ticket.id]);

  // Quando o modal fecha OU o componente desmonta (troca de rota / ticket
  // mudou), aborta a requisição em progresso. Principal motivação: quando
  // o usuario clica fora e fecha o peek, o request em curso não continua
  // para depois tentar dar setState em componente já desmontado.
  useEffect(() => {
    let cleanup = null;
    if (aberto) {
      // carregar() retorna uma função de cleanup do abort.
      cleanup = carregar();
    }
    return () => {
      if (typeof cleanup === "function") cleanup();
    };
  }, [aberto, carregar]);

  useEffect(() => {
    if (mensagens.length) {
      fimRef.current?.scrollIntoView({ block: "end" });
    }
  }, [mensagens]);

  return (
    <>
      <button
        type="button"
        aria-label={`Espiar a conversa de ${ticket.contact?.name}`}
        title="Espiar conversa"
        onClick={(e) => {
          // O item da lista abre o ticket ao clicar; espiar não pode
          // disparar isso junto.
          e.stopPropagation();
          setAberto(true);
        }}
        className="shrink-0 text-[#0872B9] transition-opacity hover:opacity-70 dark:text-sky-300"
      >
        <Eye className="h-4 w-4" />
      </button>

      {/* Wrapper anti-propagação: o Dialog do Radix/shadcn renderiza por portal
          no document.body, mas os eventos SINTÉTICOS do React continuam a
          borbulhar pela árvore virtual até chegar no pai real (o wrapper do
          TicketListItem) que tem onClick → navigate("/tickets/:id"). Esse
          <div> captura clique/mousedown/pointerdown na árvore React antes que
          eles escapem para o item da lista e evita 100% o bug "cliquei fora
          do modal e abriu o ticket sem querer". Funciona em overlay escuro,
          ESC, X do canto, pointerdown em qualquer borda. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Dialog
          open={aberto}
          onOpenChange={(v) => {
            if (!v) {
              fecharDialogo();
            } else {
              setAberto(true);
            }
          }}
        >
          <DialogContent
            className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <DialogHeader className="border-b px-4 py-3">
              <DialogTitle className="truncate text-base">
                {ticket.contact?.name}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                Só leitura — o atendimento continua com quem está nele.
              </p>
            </DialogHeader>

            <div className="flex-1 space-y-2 overflow-y-auto p-4">
            {carregando ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : mensagens.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                Nenhuma mensagem nesta conversa.
              </p>
            ) : (
              mensagens.map((m) => (
                <div
                  key={m.id}
                  className={cn(
                    "flex",
                    m.fromMe ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-2xl px-3 py-2",
                      m.fromMe ? "bg-primary/10" : "bg-muted",
                    )}
                  >
                    {m.mediaUrl && m.mediaType === "audio" ? (
                      <div className="mb-1 min-w-[250px]">
                        <Audio url={m.mediaUrl} />
                      </div>
                    ) : null}

                    {m.mediaUrl &&
                    (m.mediaType === "video") ? (
                      <video
                        src={m.mediaUrl}
                        controls
                        className="mb-1 max-h-60 max-w-full rounded-lg bg-black"
                      />
                    ) : null}

                    {m.mediaUrl &&
                    (m.mediaType === "image" || m.mediaType === "sticker") ? (
                      <img
                        src={m.mediaUrl}
                        alt=""
                        className="mb-1 max-h-40 rounded-lg object-cover"
                      />
                    ) : null}

                    {m.mediaUrl &&
                    !["audio","video","image","sticker"].includes(m.mediaType) ? (
                      <div className="mb-2">
                        <a
                          href={m.mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded border bg-background px-2 py-1 text-xs font-medium text-foreground/90 underline-offset-4 hover:underline"
                        >
                          {m.mediaType || "Arquivo"} &middot; Abrir ↗
                        </a>
                      </div>
                    ) : null}

                    <div className="whitespace-pre-line wrap-break-word text-sm">
                      {m.body ? (
                        <MarkdownWrapper>{m.body}</MarkdownWrapper>
                      ) : !["audio","video","image","sticker"].includes(m.mediaType) ? (
                        // Para mídias que não tem preview (pdf/doc/etc) nós já
                        // mostram o link acima; para audio/video/img não
                        // precisamos reescrever [mídia] como texto placeholder
                        // pois o componente visual já está acima.
                        m.mediaType ? null : (
                          <span className="text-muted-foreground">[mídia]</span>
                        )
                      ) : null}
                    </div>

                    <time className="mt-0.5 block text-[10px] tabular-nums text-muted-foreground">
                      {format(parseISO(m.createdAt), "dd/MM HH:mm")}
                    </time>
                  </div>
                </div>
              ))
            )}
            <div ref={fimRef} />
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </>
  );
};

export default TicketPeekDialog;
